import { CommonModule } from '@angular/common';
import { Component, OnDestroy, Signal, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Visit, VisitDetailPhotos } from '@types-lib';
import { LoaderSpinnerComponent } from '@ui-lib';
import { VisitDetailPhotoStore } from '@visit-detail-photos-store';
import { ENVIRONMENT } from 'environments';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { Constants } from '../../../../../../../../../../../angularJs/src/app/services/constants.service';
import { FileDirective } from '../../../../../../../../../directives/file.directive';
import { GalleryDialogComponent } from '../../../components/gallery/gallery-dialog.component';
import { rejectionNoteValidatorFactory } from '@shared-utils';
import { getAiDermTypeFromFilename } from '@shared-utils';

@Component({
  selector: 'ai-derm',
  standalone: true,
  imports: [CommonModule, FileDirective, FormsModule, ReactiveFormsModule, LoaderSpinnerComponent],
  templateUrl: './ai-derm.component.html',
  styleUrls: ['./ai-derm.component.scss'],
})
export class AiDermComponent implements OnDestroy {
  visit = input<Visit.Visit>();
  approve = output<VisitDetailPhotos.PropsPayload>();
  reject = output<VisitDetailPhotos.PropsPayload>();
  readonly disableCTA = input<boolean | null>(false);
  public store = inject(VisitDetailPhotoStore);
  public environment = inject(ENVIRONMENT);
  public constants = inject(Constants);
  public dialog = inject(MatDialog);

  photos: Signal<VisitDetailPhotos.Photo[]> = this.store.examPhotosWithNoDuplicateFiles;
  diagnosis = this.store.diagnosis;
  error = this.store.error;
  subscriptions = new Subscription();
  hasEmptyRejectionNote = computed<boolean>(() => {
    this.formChangesTrigger();
    return !!this.photos()?.some(
      (photo: VisitDetailPhotos.Photo) =>
        photo.rejected && this.photoFormGroups().get(photo.id)?.get('rejectionNote')?.invalid,
    );
  });
  hasRejected = computed(() => this.photos().some(photo => photo.rejected));
  hasDeleted = computed(() => this.photos().some(photo => photo.deleted));
  photoFilesAlreadySavedBeSide = computed<VisitDetailPhotos.PhotoFileAlreadySavedBeSide[] | undefined>(
    () => this.visit()?.ai_derm?.data,
  );
  photoFormGroups = computed<Map<number, FormGroup>>(
    () =>
      new Map(
        this.photos()?.map(photo => {
          const patientFirstName = this.visit()?.user?.firstname;
          const rejectionNoteDefault =
            !photo.file.rejection_note && patientFirstName
              ? `Hello ${patientFirstName}, \n\n`
              : photo.file.rejection_note;

          // active when this specific photo is marked as rejected
          const isActive = () => !!this.photos().find(p => p.id === photo.id)?.rejected;

          const ctrl = new FormControl<string>(
            { value: rejectionNoteDefault, disabled: !this.isPending() || !isActive() },
            {
              validators: [rejectionNoteValidatorFactory(patientFirstName, isActive, 3)],
            },
          );

          return [
            photo.id,
            new FormGroup({
              rejectionNote: ctrl,
            }),
          ];
        }),
      ),
    {
      equal: (prec, curr) => this.photos().every(photo => prec.has(photo.id)),
    },
  );

  // changes to FormControls inside an array or map aren't tracked aren't directly tracked in computed signals
  // so we need to manually notify computed signals of a form change manually - Probably resolved in Angular 20 with SignalForms
  formChangesTrigger = signal(0);

  isUserDeleted = computed(() => this.visit()?.user?.status === 2);
  isPending = computed<boolean | undefined>(() =>
    this.photos().every(
      (photo: VisitDetailPhotos.Photo) => photo.file.status === this.constants.PhotoStatuses.PendingReview,
    ),
  );
  isApproved = computed(() => this.photos().every(p => p.file?.status === this.constants.PhotoStatuses.Approved));
  waitingForUpload = computed(() => this.photos().some(p => p.file?.status === this.constants.PhotoStatuses.Rejected));
  approveCTADisabled = computed(() => this.hasRejected() || this.hasDeleted() || this.disableCTA());
  rejectCTADisabled = computed(() => !this.hasRejected() || this.hasEmptyRejectionNote());
  readonly STATUS_REJECTED = VisitDetailPhotos.PhotoStatuses.REJECTED;
  galleryDialogRef?: MatDialogRef<GalleryDialogComponent>;

  rotationMap: Record<number, number> = {};

  constructor() {
    effect(() => {
      this.photos().forEach(photo => {
        if (photo.file.status === this.constants.PhotoStatuses.Rejected) {
          this.photoFormGroups().get(photo.id)?.get('rejectionNote')?.disable();
        }
      });
    });
  }

  onBase64ImageRerender(photo: VisitDetailPhotos.Photo, base64Img: string) {
    if (this.shouldSkipProcessing(photo)) return;

    const alreadySaved = this.isPhotoAlreadySaved(photo.file.id);
    if (alreadySaved) {
      this.store.getAiDermResults(photo.file.id);
      return;
    }

    this.processNewPhoto(photo, base64Img);
  }

  private shouldSkipProcessing(photo: VisitDetailPhotos.Photo): boolean {
    return !!photo.aiDermInfo || photo.file.status === VisitDetailPhotos.PhotoStatuses.REJECTED;
  }

  private isPhotoAlreadySaved(fileId: number): boolean {
    return (this.photoFilesAlreadySavedBeSide() ?? []).some(file => file.file_id === fileId);
  }

  private processNewPhoto(photo: VisitDetailPhotos.Photo, base64Img: string) {
    if (!this.isValidBase64Image(base64Img)) {
      console.warn('[AiDerm] base64Img is not a valid image format for file_id:', photo.file.id);
      return;
    }
    const truncatedBase64 = this.extractBase64Data(base64Img);
    let filename = photo.file.filename;
    if (!filename) {
      const mimeTypeMatch = base64Img.match(/data:image\/(\w+);base64,/);
      const mimeType = mimeTypeMatch?.[1] || 'jpg';
      const normalizedType = this.normalizeMimeType(mimeType);
      filename = `image.${normalizedType === 'jpeg' ? 'jpg' : normalizedType}`;
    }
    this.processWithTypeValidation(photo, base64Img, truncatedBase64, filename);
  }

  private isValidBase64Image(base64Img: string): boolean {
    return base64Img?.startsWith('data:image');
  }

  private extractBase64Data(base64Img: string): string {
    return base64Img.replace(/data:image\/.*;base64,/g, '');
  }

  private processWithTypeValidation(
    photo: VisitDetailPhotos.Photo,
    base64Img: string,
    truncatedBase64: string,
    filename: string,
  ) {
    const typeValidation = this.validateFileType(base64Img, filename);

    if (!typeValidation.isValid) {
      this.handleFileTypeMismatch(photo.file.id, typeValidation.filenameType, typeValidation.mimeType);
      return;
    }

    const payload: VisitDetailPhotos.AiDermPostRequest = {
      file_id: photo.file.id,
      image: truncatedBase64,
      filename,
      type: typeValidation.resolvedType,
    };
    this.store.setAiDermResults(payload);
  }

  private validateFileType(base64Img: string, filename: string) {
    const mimeTypeMatch = base64Img.match(/data:image\/(\w+);base64,/);
    const rawMimeType = mimeTypeMatch?.[1] || null;
    const mimeType = rawMimeType ? this.normalizeMimeType(rawMimeType) : null;
    const filenameType = getAiDermTypeFromFilename(filename);
    const resolvedType = mimeType || filenameType;

    return {
      isValid: !mimeType || filenameType === mimeType,
      mimeType,
      filenameType,
      resolvedType,
    };
  }

  private normalizeMimeType(mimeType: string): string {
    return mimeType === 'jpg' ? 'jpeg' : mimeType;
  }

  private handleFileTypeMismatch(fileId: number, filenameType: string, mimeType: string | null) {
    console.error(
      '[AiDerm] File type mismatch detected! filename type:',
      filenameType,
      'mime type:',
      mimeType,
      'for file_id:',
      fileId,
    );

    this.store.setExamPhotos(
      this.photos().map(p =>
        p.file.id === fileId
          ? {
              ...p,
              aiDermInfo: {
                file_id: fileId,
                error:
                  'File type mismatch: filename extension does not match image format. Please reject and request a new photo.',
              },
            }
          : p,
      ),
    );
  }

  private processLegacyFormat(fileId: number, truncatedBase64: string) {
    const payload = {
      file_id: fileId,
      image: truncatedBase64,
    };
    this.store.setAiDermResults(payload as any);
  }

  formatTime(timestamp?: number): string {
    if (!timestamp) return '';
    return moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
  }

  onApprove() {
    const payload = {
      photos: (this.photos() as VisitDetailPhotos.Photo[]).map((photo: VisitDetailPhotos.Photo) => {
        return {
          ...photo,
          file: {
            ...photo.file,
            rejection_note: this.photoFormGroups().get(photo.id)?.get('rejectionNote')?.value,
          },
        };
      }),
    };
    this.approve.emit({ payload });
  }

  onReject() {
    if (this.hasEmptyRejectionNote()) {
      console.warn('Please provide a rejection message');
      return;
    }
    const payload = {
      photos: (this.photos() as VisitDetailPhotos.Photo[]).map((photo: VisitDetailPhotos.Photo) => {
        return {
          ...photo,
          file: {
            ...photo.file,
            rejection_note: this.photoFormGroups().get(photo.id)?.get('rejectionNote')?.value,
          },
        };
      }),
    };
    this.reject.emit({ payload });
  }

  isPhotoAiDermLoading(photoId: number): boolean {
    return this.store.loadingAiDermMap?.()[photoId] ?? false;
  }

  setRejectStatus(id: number, status: boolean) {
    this.store.setRejectedStatus(id, status);
    const fg = this.photoFormGroups().get(id);
    const ctrl = fg?.get('rejectionNote');
    if (ctrl) {
      const pending = !!this.isPending();
      if (status && pending) ctrl.enable({ emitEvent: false });
      else ctrl.disable({ emitEvent: false });
      ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      this.onFormControlChange(fg);
    }
  }

  rotate(photoId: number) {
    const current = this.rotationMap[photoId] ?? 0;
    this.rotationMap[photoId] = (current + 90) % 360;
  }

  getRotation(photoId: number): string {
    const degrees = this.rotationMap[photoId] ?? 0;
    return `rotate(${degrees}deg)`;
  }

  openPhoto(index: number) {
    if (!this.galleryDialogRef) {
      this.galleryDialogRef = this.dialog.open(GalleryDialogComponent, {
        hasBackdrop: false,
        data: {
          showCloseIcon: true,
          files: [...this.photos()],
          index,
          requiresAction: this.requiresAction.bind(this),
        },
      });

      this.subscriptions.add(
        this.galleryDialogRef.afterClosed().subscribe((photoGalleryPhotos: VisitDetailPhotos.Photo[]) => {
          this.galleryDialogRef = undefined;
          this.store.setExamPhotos([...photoGalleryPhotos]);
        }),
      );
    }
  }

  onFormControlChange(formGroup?: FormGroup) {
    formGroup?.updateValueAndValidity();
    this.formChangesTrigger.update(n => (n + 1) % 2);
  }

  requiresAction(): boolean {
    return !!this.isPending();
  }

  ngOnDestroy(): void {
    if (this.galleryDialogRef) {
      this.galleryDialogRef.close();
    }
    this.subscriptions.unsubscribe();
  }
}
